import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useOrganization } from "../OrganizationContext"
import { useUser } from "../UserContext"
import { useUserRole } from "../UserRoleContext"
import {
  createMonitoringClient,
  fetchMonitoringClients,
  fetchOrganizationMonitoringRole,
} from "../api/monitoring"
import { completeMonitoringGoogleOAuth } from "../api/monitoringGoogle"
import { completeMeasurementOAuth } from "../api/measurementDocumentation"
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui"

const defaultForm = {
  name: "",
  monitoringProfile: "custom_baseline",
  monitoringTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
  monitoringFrequency: "daily",
}

const healthVariant = (health) => health === "healthy" ? "accent" : health === "critical" ? "outline" : "subtle"
const activeIssues = (issues = []) => issues.filter((issue) =>
  !["resolved", "ignored", "false_positive"].includes(issue.status),
)

export default function Clients() {
  const { selectedOrgId } = useOrganization()
  const user = useUser()
  const globalRole = useUserRole()
  const navigate = useNavigate()
  const [clients, setClients] = useState([])
  const [memberRole, setMemberRole] = useState(null)
  const [status, setStatus] = useState("loading")
  const [error, setError] = useState("")
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState(defaultForm)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState("")
  const [profile, setProfile] = useState("all")
  const [health, setHealth] = useState("all")
  const oauthHandled = useRef(false)

  useEffect(() => {
    const query = new URLSearchParams(window.location.search)
    const oauthProvider = query.get("oauth_provider")
    if (
      oauthHandled.current ||
      !["monitoring_google", "measurement_documentation"].includes(oauthProvider) ||
      !query.get("state") ||
      !selectedOrgId
    ) return
    oauthHandled.current = true
    if (oauthProvider === "measurement_documentation") {
      sessionStorage.removeItem("measurement-documentation-oauth-pending")
    }
    if (query.get("error")) {
      setError(query.get("error_description") || "Google authorization was cancelled.")
      window.history.replaceState({}, document.title, "/clients")
      return
    }
    setStatus("loading")
    const completeOAuth = oauthProvider === "measurement_documentation"
      ? completeMeasurementOAuth
      : completeMonitoringGoogleOAuth
    completeOAuth(selectedOrgId, query.get("code"), query.get("state"))
      .then((result) => navigate(
        oauthProvider === "measurement_documentation"
          ? `/accounts/${result.account_id}/connections`
          : `/accounts/${result.account_id}/connections?google_connection=${result.oauth_connection.id}`,
        { replace: true },
      ))
      .catch((oauthError) => {
        setError(oauthError.message || "Unable to complete Google authorization.")
        setStatus("error")
        window.history.replaceState({}, document.title, "/clients")
      })
  }, [navigate, selectedOrgId])

  const load = useCallback(async () => {
    if (!selectedOrgId || !user?.id) return
    setStatus("loading")
    try {
      const [rows, role] = await Promise.all([
        fetchMonitoringClients(selectedOrgId),
        fetchOrganizationMonitoringRole(selectedOrgId, user.id),
      ])
      setClients(rows)
      setMemberRole(role)
      setError("")
      setStatus("ready")
    } catch (loadError) {
      setError(loadError.message || "Unable to load clients.")
      setStatus("error")
    }
  }, [selectedOrgId, user?.id])

  useEffect(() => { load() }, [load])

  const canManage = globalRole === "admin" || ["admin", "member"].includes(memberRole)
  const filtered = useMemo(() => clients.filter((client) => {
    if (profile !== "all" && client.monitoring_profile !== profile) return false
    if (health !== "all" && client.health_status !== health) return false
    return client.name.toLowerCase().includes(search.trim().toLowerCase())
  }), [clients, health, profile, search])

  const submit = async (event) => {
    event.preventDefault()
    if (!form.name.trim()) return
    setSaving(true)
    setError("")
    try {
      const created = await createMonitoringClient(selectedOrgId, form)
      setShowCreate(false)
      setForm(defaultForm)
      navigate(`/clients/${created.id}`)
    } catch (saveError) {
      setError(saveError.message || "Unable to create the client.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="page-container page-container--wide ui-stack ui-stack--xl">
      <div className="ui-page-header">
        <div className="ui-stack ui-stack--xs">
          <h1 className="ui-page-title">Conversion monitoring clients</h1>
          <p className="ui-subtitle">
            Each client is checked after 5:00 AM in its reporting timezone using data through the previous local day.
          </p>
        </div>
        {canManage && <Button onClick={() => setShowCreate((value) => !value)}>{showCreate ? "Cancel" : "Add client"}</Button>}
      </div>

      {error && <p className="ui-error" role="alert">{error}</p>}

      {showCreate && (
        <Card>
          <CardHeader>
            <CardTitle>Add a client</CardTitle>
            <CardDescription>
              Custom baseline is the default. Automotive adds ASC rules only when the organization has that entitlement.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="ui-grid ui-grid--form" onSubmit={submit}>
              <Input label="Client name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
              <label className="ui-field">
                <span className="ui-label">Monitoring profile</span>
                <select className="ui-input" value={form.monitoringProfile} onChange={(event) => setForm({ ...form, monitoringProfile: event.target.value })}>
                  <option value="custom_baseline">Custom baseline</option>
                  <option value="automotive_asc">Automotive + ASC</option>
                </select>
              </label>
              <Input
                label="Reporting timezone"
                hint="IANA name, for example America/Chicago"
                value={form.monitoringTimezone}
                onChange={(event) => setForm({ ...form, monitoringTimezone: event.target.value })}
                required
              />
              <label className="ui-field">
                <span className="ui-label">Frequency</span>
                <select className="ui-input" value={form.monitoringFrequency} onChange={(event) => setForm({ ...form, monitoringFrequency: event.target.value })}>
                  <option value="daily">Daily</option>
                  <option value="every_3_days">Every three days</option>
                  <option value="weekly">Weekly</option>
                </select>
              </label>
              <div className="ui-button-row"><Button type="submit" disabled={saving}>{saving ? "Creating…" : "Create client"}</Button></div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Client health</CardTitle>
          <CardDescription>Unknown means current evidence or an approved baseline is unavailable.</CardDescription>
        </CardHeader>
        <CardContent className="ui-stack ui-stack--md">
          <div className="ui-grid ui-grid--form">
            <Input label="Search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Client name" />
            <label className="ui-field">
              <span className="ui-label">Profile</span>
              <select className="ui-input" value={profile} onChange={(event) => setProfile(event.target.value)}>
                <option value="all">All profiles</option>
                <option value="custom_baseline">Custom baseline</option>
                <option value="automotive_asc">Automotive + ASC</option>
              </select>
            </label>
            <label className="ui-field">
              <span className="ui-label">Health</span>
              <select className="ui-input" value={health} onChange={(event) => setHealth(event.target.value)}>
                <option value="all">All health states</option>
                <option value="healthy">Healthy</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
                <option value="unknown">Unknown</option>
              </select>
            </label>
          </div>
          {status === "loading" ? <p className="ui-text--muted">Loading clients…</p> : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Client</TableHeaderCell>
                    <TableHeaderCell>Profile</TableHeaderCell>
                    <TableHeaderCell>Health</TableHeaderCell>
                    <TableHeaderCell>Connections</TableHeaderCell>
                    <TableHeaderCell>Open issues</TableHeaderCell>
                    <TableHeaderCell>Next run</TableHeaderCell>
                    <TableHeaderCell align="right">Action</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell>{client.name}</TableCell>
                      <TableCell>{client.monitoring_profile === "automotive_asc" ? "Automotive + ASC" : "Custom baseline"}</TableCell>
                      <TableCell><Badge variant={healthVariant(client.health_status)}>{client.health_status}</Badge></TableCell>
                      <TableCell>{client.platform_accounts?.filter((item) => item.active).length || 0}</TableCell>
                      <TableCell>{activeIssues(client.issues).length}</TableCell>
                      <TableCell>{client.next_scan_at ? new Date(client.next_scan_at).toLocaleString() : "Not scheduled"}</TableCell>
                      <TableCell align="right"><Button as={Link} to={`/clients/${client.id}`} size="sm" variant="outline">View</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {status === "ready" && !filtered.length && <p className="ui-text--muted">No clients match these filters.</p>}
          {status === "error" && <Button variant="outline" onClick={load}>Retry</Button>}
        </CardContent>
      </Card>
    </div>
  )
}
